import * as FileSaver from "file-saver";
import moment from "moment";
import { Workbook } from "exceljs";
import { dateIsValid } from "./dateIsValid";
import { formatDate } from "./MomentHelper";

const ExcelDefaultWidth = 35;

// Kolon genişliğini dinamik olarak hesaplayan fonksiyon
const calculateColumnWidth = (data: any[], columnIndex: number, headerText: string): number => {
  const MAX_WIDTH = 50;
  const PADDING = 3;
  const HEADER_BOLD_MULTIPLIER = 2; // Bold header'lar daha fazla yer kaplar

  // 1. Header uzunluğunu bold faktörü ile hesapla
  const headerWidth = Math.ceil(headerText.length * HEADER_BOLD_MULTIPLIER);
  let maxDataLength = 0;

  // 2. Veri içeriğindeki maksimum uzunluğu bul
  data.forEach(row => {
    const keys = Object.keys(row);
    if (keys[columnIndex]) {
      const cellValue = row[keys[columnIndex]];
      if (cellValue != null) {
        const cellLength = String(cellValue).length;
        maxDataLength = Math.max(maxDataLength, cellLength);
      }
    }
  });

  // 3. Header ve data'nın maksimumunu al, padding ekle ve MAX_WIDTH ile sınırla
  const maxLength = Math.max(headerWidth, maxDataLength);
  return Math.min(maxLength + PADDING, MAX_WIDTH);
};

interface CellStyle {
  fill?: {
    type: "pattern";
    pattern: "solid";
    fgColor: { argb: string };
  };
  font?: {
    bold?: boolean;
    color?: { argb: string };
  };
}

interface RowStyleConfig {
  rowStyleCondition?: (row: any[], rowIndex: number) => boolean;
  rowStyle?: CellStyle;
  cellStyleConfigs?: Array<{
    condition: (row: any[], rowIndex: number, colIndex: number) => boolean;
    style: CellStyle;
  }>;
}

const getCustomerFilterValue = (headers: any, data: any, user: any) => {
  if (user?.isCustomer == false || !user) {
    return { headers: headers, data: data };
  }
  const index = headers.findIndex(x => x.isCustomer === false);
  if (index >= 0) {
    headers.splice(index, 1);
    data = data.map(x => {
      x.splice(index, 1);
      return x;
    });
  }

  return headers.findIndex(x => x.isCustomer === false) >= 0
    ? getCustomerFilterValue([...headers], [...data], user)
    : { headers: headers, data: data };
};
export const ExcelExport = (
  headers,
  data: any = [],
  source: string = "Source",
  user: any = null,
  styleConfig?: RowStyleConfig,
  includeTimezone: boolean = true
) => {
  var values = getCustomerFilterValue([...headers], [...data], user);
  headers = values.headers.map(header =>
    typeof header === "string" ? { header: header, key: header.toLowerCase().replace(/\s+/g, "_") } : header
  );
  data = values.data;
  let workbook = new Workbook();
  let worksheet = workbook.addWorksheet(source);

  // Kolon genişliklerini otomatik ayarla
  headers = headers.map((head, index) => {
    if (head.width === undefined) {
      const headerText = head.header || "";
      head.width = calculateColumnWidth(data, index, headerText);
    }
    return head;
  });

  if (includeTimezone) {
    headers.push({
      header: "TIMEZONE",
      key: "TIMEZONE",
      width: ExcelDefaultWidth,
    });
  }

  worksheet.columns = headers;

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone + " " + moment.utc(new Date()).local().format("Z");

  let dataRowIndex = 0;
  for (let x1 of data) {
    let x2 = Object.keys(x1);
    let temp: any = [];
    for (let y of x2) {
      let data = x1[y];
      if (dateIsValid(String(data))) {
        data = formatDate(data, true, "D.MM.yyyy HH:mm");
      }
      temp.push(data);
    }

    if (includeTimezone) {
      temp.push(timeZone);
    }
    const row = worksheet.addRow(temp);

    // Koşullu satır stilini uygula
    if (styleConfig && styleConfig.rowStyleCondition && styleConfig.rowStyleCondition(temp, dataRowIndex)) {
      row.eachCell({ includeEmpty: true }, cell => {
        if (styleConfig.rowStyle?.fill) {
          cell.fill = styleConfig.rowStyle.fill;
        }
        if (styleConfig.rowStyle?.font) {
          cell.font = { ...cell.font, ...styleConfig.rowStyle.font };
        }
      });
    }

    // Koşullu hücre stilini uygula
    if (styleConfig?.cellStyleConfigs?.length) {
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const colIndex = colNumber - 1;
        styleConfig.cellStyleConfigs!.forEach(cfg => {
          if (cfg.condition(temp, dataRowIndex, colIndex)) {
            if (cfg.style.fill) cell.fill = cfg.style.fill;
            if (cfg.style.font) cell.font = { ...cell.font, ...cfg.style.font };
          }
        });
      });
    }

    dataRowIndex++;
  }

  // AutoFilter ekle (Excel'de sütun filtreleme)
  if (data.length > 0) {
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: data.length + 1, column: headers.length },
    };
  }
  // Header satırını sabitle (Freeze Panes)
  worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2" }];
  worksheet.getRow(1).eachCell({ includeEmpty: false }, function (cell, rowNumber) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE2EFDA" },
      bgColor: { argb: "cccccc" },
    };
    cell.font = { bold: true, size: 16 };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });

  worksheet.eachRow({ includeEmpty: true }, function (row, rowNumber) {
    row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };

      if (dateIsValid(String(cell.value))) {
        cell.numFmt = "d.mm.yyyy hh:mm";
      }
    });
  });

  workbook.xlsx.writeBuffer().then(data => {
    let blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    FileSaver.saveAs(blob, `${source} - ${formatDate(Date.now())}.xlsx`);
  });
};
